import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { navFor, hrefFor, locationHref, LOCATION_ICON } from "./navConfig";

// Label prefix > word prefix > label substring > description/group match.
function score(q, label, extra) {
  const l = label.toLowerCase();
  if (l.startsWith(q)) return 4;
  if (l.split(/[^a-z0-9]+/).some((w) => w.startsWith(q))) return 3;
  if (l.includes(q)) return 2;
  return extra.toLowerCase().includes(q) ? 1 : 0;
}

export function QuickSearch({ open, setOpen, role, locations, location, setLocation }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);
  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const entries = useMemo(() => [
    ...navFor(role).flatMap((g) => g.items.map((i) => ({ id: i.to, group: g.label || "Start", label: i.label, extra: `${i.desc} ${g.label || ""}`, icon: i.icon, desc: i.desc, to: hrefFor(i, location) }))),
    ...locations.map((l) => ({ id: `loc:${l}`, group: "Locations", label: l, extra: "location", icon: LOCATION_ICON, desc: "Open location & filter pages", loc: l, to: locationHref(l) })),
  ], [role, locations, location]);

  const q = query.trim().toLowerCase();
  const results = q ? entries.map((e) => ({ ...e, s: score(q, e.label, e.extra) })).filter((e) => e.s > 0).sort((a, b) => b.s - a.s) : null;
  const groups = results ? [["Results", results]] : Object.entries(entries.reduce((m, e) => ((m[e.group] = m[e.group] || []).push(e), m), {}));

  const firstId = groups[0]?.[1]?.[0]?.id || "";
  const [selected, setSelected] = useState("");
  useEffect(() => { setSelected(firstId); }, [q, firstId]);
  const go = (e) => { setOpen(false); if (e.loc) setLocation(e.loc); navigate(e.to); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0" data-testid="quick-search-dialog">
        <DialogTitle className="sr-only">Find a page</DialogTitle>
        <DialogDescription className="sr-only">Search pages and locations</DialogDescription>
        <Command shouldFilter={false} value={selected} onValueChange={setSelected} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-brand-tertiary [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5">
          <CommandInput value={query} onValueChange={setQuery} placeholder="Search pages or locations…" data-testid="quick-search-input" />
          <CommandList data-testid="quick-search-list" className="max-h-[60vh]">
            <CommandEmpty>Nothing found.</CommandEmpty>
            {groups.map(([name, list]) => (
              <CommandGroup key={`${name}-${q}`} heading={name}>
                {list.map((e) => (
                  <CommandItem key={e.id} value={e.id} onSelect={() => go(e)} data-testid={e.loc ? `quick-search-loc-${e.loc.replace(/\W+/g, "-")}` : `quick-search-item-${e.id.replace(/\W+/g, "") || "financials"}`}>
                    <e.icon className="h-4 w-4 mr-2 text-brand-tertiary shrink-0" />
                    <span className="text-brand-ink">{e.label}</span>
                    <span className="ml-2 text-xs text-brand-tertiary truncate">{e.desc}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
